package ai.jessie.web.pages

import ai.jessie.web.theme.AlertListStyle
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.ButtonSize
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CardHeadRowStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.ChoiceCardStyle
import ai.jessie.web.theme.ChoiceCardsStyle
import ai.jessie.web.theme.ChoiceHintStyle
import ai.jessie.web.theme.DefinitionsStyle
import ai.jessie.web.theme.DocumentItemMetaStyle
import ai.jessie.web.theme.DocumentItemStyle
import ai.jessie.web.theme.DocumentItemTextStyle
import ai.jessie.web.theme.DocumentItemTitleStyle
import ai.jessie.web.theme.DocumentListStyle
import ai.jessie.web.theme.DocumentThumbStyle
import ai.jessie.web.theme.FieldErrorStyle
import ai.jessie.web.theme.FieldInputStyle
import ai.jessie.web.theme.FieldStyle
import ai.jessie.web.theme.FieldsetStyle
import ai.jessie.web.theme.FormStackStyle
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.MutedStyle
import ai.jessie.web.theme.NoticeStyle
import ai.jessie.web.theme.NumInputStyle
import ai.jessie.web.theme.ProgressStepsStyle
import ai.jessie.web.theme.SmallStyle
import ai.jessie.web.theme.StatBarStyle
import ai.jessie.web.theme.StatValueStyle
import ai.jessie.web.theme.StatusStyle
import ai.jessie.web.theme.TextCardStyle
import ai.jessie.web.theme.card
import ai.jessie.web.theme.crumb
import ai.jessie.web.theme.disclosure
import ai.jessie.web.theme.number
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.forId
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Legend
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement

// Card variants of the calendar (`screens/02`): a head on one line and a card that opens on demand.
@Composable
internal fun cardVariants() {
    Div(attrs = CardStyle.toAttrs()) {
        Div(attrs = CardHeadRowStyle.toAttrs()) {
            H2 { Text("Freitag, 18. September 2026") }
            Span { number("8.25", "h") }
            themedButton("Neue Arbeit", {}, variant = ButtonVariant.TINT, size = ButtonSize.SMALL)
        }
    }
    disclosure("Zeitraum erfassen") {
        P(attrs = HintStyle.toAttrs()) {
            Text("Der Inhalt bleibt im DOM, solange die Karte zu ist.")
        }
    }
}

// Komponenten II (`screens/24`, `screens/25`). Static samples carry no `role="alert"`/`"status"`:
// nothing changes on this page, so there is nothing to announce.
@Composable
internal fun feedback() {
    card(
        "Rückmeldungen",
        "Alert nennt die Korrektur, Notice bestätigt, Status ist nie rot, Hint erklärt ohne Warnung.",
    ) {
        Div(attrs = AlertStyle.toAttrs()) {
            Text("Die Position «Fahrzeit» ist nicht mehr aktiv. ")
            A(href = "#") { Text("Position wechseln") }
            Text(" oder Eintrag verwerfen.")
        }
        P(attrs = NoticeStyle.toAttrs()) { Text("Arbeit vom 18. September gespeichert.") }
        P(attrs = StatusStyle.toAttrs()) { Text("Wird geladen oder gespeichert …") }
        P(attrs = HintStyle.toAttrs()) {
            Text(
                "Nicht verrechenbare Positionen können z.B. Aufwände für Offerten, Verkauf oder " +
                    "Reisezeit sein."
            )
        }
        Ul(attrs = AlertListStyle.toAttrs()) {
            Li { Text("Stunden: Zwischen 0.25 und 24 Stunden eingeben.") }
            Li { Text("Position: Eine aktive Position wählen.") }
        }
        Label(forId = "sg-hours", attrs = FieldStyle.toAttrs()) {
            Text("Stunden")
            Input(
                InputType.Text,
                attrs =
                    NumInputStyle.toAttrs {
                        id("sg-hours")
                        value("26")
                        attr("aria-invalid", "true")
                        attr("aria-describedby", "sg-hours-error")
                    },
            )
        }
        P(attrs = FieldErrorStyle.toAttrs { id("sg-hours-error") }) {
            Text("Zwischen 0.25 und 24 Stunden eingeben.")
        }
    }
}

@Composable
internal fun formGroups() {
    var pricing by remember { mutableStateOf("effort") }
    card("Formular-Gruppen und Auswahl", "Gruppen ohne Rahmen; Auswahl als Karten.") {
        Div(attrs = listOf(TextCardStyle, FormStackStyle).toAttrs()) {
            Fieldset(attrs = listOf(FieldsetStyle, FormStackStyle).toAttrs()) {
                Legend { Text("Adresse") }
                P(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) {
                    Text("Wird als Absender auf Rechnungen verwendet.")
                }
                Label(forId = "sg-street", attrs = FieldStyle.toAttrs()) {
                    Text("Adresszeile 1")
                    Input(
                        InputType.Text,
                        attrs =
                            FieldInputStyle.toAttrs {
                                id("sg-street")
                                value("Untere Vogelsangstrasse 205")
                            },
                    )
                }
            }
            Fieldset(attrs = FieldsetStyle.toAttrs()) {
                Legend { Text("Preismodell") }
                Div(attrs = ChoiceCardsStyle.toAttrs()) {
                    choiceCard(
                        "effort",
                        "Aufwand",
                        "Preis ergibt sich aus den gebuchten Stunden × Stundensatz.",
                        pricing,
                    ) {
                        pricing = it
                    }
                    choiceCard(
                        "fixed",
                        "Fixpreis",
                        "Wird bis zum Festpreis verrechnet; Stunden nur zur Kostenkontrolle.",
                        pricing,
                    ) {
                        pricing = it
                    }
                }
            }
        }
    }
}

@Composable
private fun choiceCard(
    value: String,
    title: String,
    description: String,
    selected: String,
    choose: (String) -> Unit,
) {
    Label(attrs = ChoiceCardStyle.toAttrs { forId("sg-pricing-$value") }) {
        Input(
            InputType.Radio,
            attrs = {
                id("sg-pricing-$value")
                attr("name", "sg-pricing")
                checked(value == selected)
                onChange { choose(value) }
            },
        )
        Span {
            Text(title)
            Span(attrs = ChoiceHintStyle.toAttrs()) { Text(description) }
        }
    }
}

@Composable
internal fun recordContent() {
    card("Stammdaten", "Definitions: Begriff links, Wert rechts.") {
        TagElement<HTMLElement>("dl", DefinitionsStyle.toAttrs()) {
            listOf(
                    "Kurzname" to "Valtech",
                    "Rechtsgültig" to "Valtech Switzerland AG",
                    "Adresse" to "Stadtturmstrasse 10, 5400 Baden, CH",
                )
                .forEach { (term, value) ->
                    TagElement<HTMLElement>("dt", null) { Text(term) }
                    TagElement<HTMLElement>("dd", null) { Text(value) }
                }
        }
    }
    Div(attrs = CardStyle.toAttrs()) {
        Ul(attrs = DocumentListStyle.toAttrs { attr("aria-label", "Dokumente") }) {
            documentItem("Receipt 2271 3968", "PDF · 19.09.2026 · Anthropic", current = true)
            documentItem(
                "Lohnbeiträge Akontorechnung Nr. SS7.465 – SVA Zürich – 09.2026",
                "PDF · 09.09.2026 · SVA Zürich",
            )
            documentItem("Epson 18092026065811", "JPG · 18.09.2026")
        }
    }
}

@Composable
private fun documentItem(title: String, meta: String, current: Boolean = false) {
    Li {
        A(
            href = "#",
            attrs = DocumentItemStyle.toAttrs { if (current) attr("aria-current", "page") },
        ) {
            Span(attrs = DocumentThumbStyle.toAttrs { attr("aria-hidden", "true") }) {
                Text(meta.substringBefore(" "))
            }
            Span(attrs = DocumentItemTextStyle.toAttrs()) {
                Span(attrs = DocumentItemTitleStyle.toAttrs()) { Text(title) }
                Span(attrs = DocumentItemMetaStyle.toAttrs()) { Text(meta) }
            }
        }
    }
}

@Composable
internal fun navigationAndFigures() {
    card("Navigation und Kennzahlen", "Crumb und ProgressSteps; KPI im Seitenkopf oben.") {
        crumb("Sanitas", "#", "Rechnung Nr. 203")
        Ol(attrs = ProgressStepsStyle.toAttrs { attr("aria-label", "Statusschritte") }) {
            listOf("Positionen", "Texte", "Adressen", "Fertigstellen").forEachIndexed { index, step
                ->
                Li(
                    attrs = {
                        if (index < 1) attr("data-done", "")
                        if (index == 1) attr("aria-current", "step")
                    }
                ) {
                    Span(attrs = { attr("aria-hidden", "true") }) {
                        Text(if (index < 1) "✓" else "${index + 1}")
                    }
                    Text(step)
                }
            }
        }
    }
    Div(attrs = CardStyle.toAttrs()) {
        TagElement<HTMLElement>("dl", StatBarStyle.toAttrs()) {
            listOf(
                    "Offeriert" to "2’001.00",
                    "Geleistet" to "150.00",
                    "Verrechnet" to "CHF 0.00",
                    "Offen" to "CHF 21’106.25",
                )
                .forEach { (label, value) ->
                    Div {
                        TagElement<HTMLElement>("dt", null) { Text(label) }
                        TagElement<HTMLElement>("dd", StatValueStyle.toAttrs()) { Text(value) }
                    }
                }
        }
    }
}
